import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useReducer } from 'react';
import { Checkbox, CheckboxGroup } from '@hub/checkbox';
import { Grid, GridItem } from '@hub/grid';
import { SearchMediumIcon } from '@hub/icon';
import { Input, InputGroup, InputLeftElement } from '@hub/input';
import { Stack } from '@hub/stack';
import { Text } from '@hub/text';
import { sortBy, debounce } from 'lodash';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { CheckboxWithLink } from './checkbox-with-link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export async function _filterOptonsBySearchTerm(_id, unfilteredOptionGroups, search) {
  return unfilteredOptionGroups.map(group => {
    var _group$options;
    const options = (_group$options = group.options) === null || _group$options === void 0 ? void 0 : _group$options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
    return _objectSpread(_objectSpread({}, group), {}, {
      options
    });
  }).filter(group => group.options && group.options.length > 0);
}
export function optionGroupsForCurrentSearchState(filteredOptionGroups, unfilteredOptionGroups, context) {
  if (!context.isSearchable) {
    return unfilteredOptionGroups;
  }
  if (context.searchTerm.trim() !== '' && filteredOptionGroups.length === 0) {
    return [];
  }
  if (filteredOptionGroups.length > 0) {
    return filteredOptionGroups;
  }
  return unfilteredOptionGroups;
}
export function getSortedOptionGroups(filteredOptionGroups, unfilteredOptionGroups, context) {
  // Server load doesn't run the useEffect so we need to use the unfiltered option groups
  const optionGroupsUnsorted = optionGroupsForCurrentSearchState(filteredOptionGroups, unfilteredOptionGroups, context);
  if (context.isSearchable) {
    return sortBy(optionGroupsUnsorted.map(group => _objectSpread(_objectSpread({}, group), {}, {
      options: sortBy(group.options, [o => o.label.toLowerCase()])
    })), [o => {
      var _o$label;
      return (_o$label = o.label) === null || _o$label === void 0 ? void 0 : _o$label.toLowerCase();
    }]);
  }
  return optionGroupsUnsorted;
}
const reduce = (state, action) => {
  switch (action.type) {
    case 'setSearchTerm':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          searchTerm: action.searchTerm
        });
      }
    case 'setFilteredOptionGroups':
      {
        if (state.lastUpdated < action.timestamp) {
          return _objectSpread(_objectSpread({}, state), {}, {
            filteredOptionGroups: action.filteredOptionGroups,
            lastUpdated: action.timestamp
          });
        } else {
          return state;
        }
      }
    default:
      {
        return state;
      }
  }
};
const DEBEOUNCED_SEARCH_DELAY = 100;
export const _filterOptions = (id, unfilteredOptionGroups, searchTerm, dispatch, onSearch) => {
  const filterOptions = async () => {
    const timestamp = Date.now();
    const filteredOptionGroups = searchTerm === '' ? unfilteredOptionGroups : await onSearch(id, unfilteredOptionGroups, searchTerm);
    dispatch({
      type: 'setFilteredOptionGroups',
      filteredOptionGroups,
      timestamp
    });
  };
  filterOptions();
};
const _debouncedFilterOptions = debounce(_filterOptions, DEBEOUNCED_SEARCH_DELAY, {
  leading: true
});
const onChangeWithSelectedOption = (previousSelection, onChange) => selectedOptions => {
  const newCheckedSelection = selectedOptions.filter(option => !previousSelection.includes(option))[0];
  const newUncheckedSelection = previousSelection.filter(option => !selectedOptions.includes(option))[0];
  const changedOption = newCheckedSelection || newUncheckedSelection;
  onChange(selectedOptions, {
    option: changedOption,
    isChecked: Boolean(newCheckedSelection)
  });
};
export const SearchableFacetedFilter = _ref => {
  let {
    id,
    title,
    searchFieldPlaceholder = "Search ".concat(title),
    optionGroups: unfilteredOptionGroups = [],
    isSearchable = false,
    isLinked = false,
    selectedOptions = [],
    onSearch = _filterOptonsBySearchTerm,
    onChange
  } = _ref;
  const {
    0: state,
    1: dispatch
  } = useReducer(reduce, {
    searchTerm: '',
    filteredOptionGroups: [],
    lastUpdated: 0
  });
  useDeepCompareEffect(() => {
    _debouncedFilterOptions(id, unfilteredOptionGroups, state.searchTerm, dispatch, onSearch);
    return () => _debouncedFilterOptions.cancel();
  }, [id, state.searchTerm, onSearch, unfilteredOptionGroups]);
  const optionGroups = getSortedOptionGroups(state.filteredOptionGroups, unfilteredOptionGroups, {
    isSearchable,
    searchTerm: state.searchTerm
  });
  const onSelectionChange = onChangeWithSelectedOption(selectedOptions, onChange);
  return /*#__PURE__*/_jsxs(Stack, {
    gap: "spacing-md",
    "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Stack",
    children: [isSearchable && /*#__PURE__*/_jsxs(InputGroup, {
      "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_InputGroup",
      children: [/*#__PURE__*/_jsx(InputLeftElement, {
        pointerEvents: "none",
        "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_InputGroup_InputLeftElement",
        children: /*#__PURE__*/_jsx(SearchMediumIcon, {
          "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_InputLeftElement_SearchMediumIcon"
        })
      }), /*#__PURE__*/_jsx(Input, {
        type: "search",
        placeholder: searchFieldPlaceholder,
        value: state.searchTerm,
        onChange: e => dispatch({
          type: 'setSearchTerm',
          searchTerm: e.target.value
        }),
        "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_InputGroup_Input"
      })]
    }), optionGroups && /*#__PURE__*/_jsx(CheckboxGroup, {
      value: selectedOptions,
      onChange: onSelectionChange,
      "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_CheckboxGroup",
      children: /*#__PURE__*/_jsx(Stack, {
        gap: "spacing-md",
        "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_CheckboxGroup_Stack",
        children: optionGroups.map(_ref2 => {
          let {
            label: groupLabel,
            options
          } = _ref2;
          return /*#__PURE__*/_jsxs(Stack, {
            gap: "spacing-md",
            "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Stack_7",
            children: [groupLabel && /*#__PURE__*/_jsx(Text, {
              tone: "strong",
              "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Text",
              children: groupLabel
            }), options && /*#__PURE__*/_jsx(_Fragment, {
              children: groupLabel ? /*#__PURE__*/_jsx(Grid, {
                templateColumns: "1fr 1fr",
                rowGap: "spacing-sm",
                "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Grid",
                children: options.map(_ref3 => {
                  let {
                    label: optionLabel,
                    value
                  } = _ref3;
                  return /*#__PURE__*/_jsx(GridItem, {
                    "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_GridItem",
                    children: /*#__PURE__*/_jsx(Checkbox, {
                      value: value,
                      "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_GridItem_Checkbox",
                      children: optionLabel.replace(/\//g, '/\u{200b}')
                    })
                  }, value);
                })
              }) : /*#__PURE__*/_jsx(Stack, {
                gap: "spacing-sm",
                "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Stack",
                children: options.map(_ref4 => {
                  let {
                    label: optionLabel,
                    value
                  } = _ref4;
                  return isLinked ? /*#__PURE__*/_jsx(CheckboxWithLink, {
                    name: id,
                    value: value,
                    onLinkClick: e => {
                      e.preventDefault();
                      onSelectionChange(selectedOptions.includes(value) ? selectedOptions.filter(x => x !== value) : [...selectedOptions, value]);
                    },
                    "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_CheckboxWithLink",
                    children: optionLabel
                  }, value) : /*#__PURE__*/_jsx(Checkbox, {
                    value: value,
                    "data-id": "searchable-faceted-filter_index_SearchableFacetedFilter_Checkbox",
                    children: optionLabel
                  }, value);
                })
              })
            })]
          }, groupLabel);
        })
      })
    })]
  });
};